import React, { useState, useEffect, useRef, useMemo } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { Button, Modal, Form, Badge, Table, Card, Row, Col, ProgressBar, Nav, Spinner, Alert } from "react-bootstrap";
import "./SalesForm.css";
import axios from "axios";
import BASE_URL from "../config";
import { MapContainer, TileLayer, Marker, useMapEvents, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { FaMapMarkerAlt, FaMapPin, FaCar, FaShareAlt, FaSignOutAlt, FaRedo, FaExclamationTriangle, FaCheckCircle } from "react-icons/fa";
import { FaBoxOpen } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { useParams } from 'react-router-dom';
// Import the OrderTracking component
import OrderTracking from './OrderTracking';
import AdminCustomers from './AdminCustomers';
import AdminVendors from './AdminVendors';
// Import back the SalesFormOrderTrack component
import SalesFormOrderTrack from './SalesFormOrderTrack';

// Fix for default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom icons for different markers
const createIcon = (color, icon = null) => {
    return L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">${icon || ''}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
    });
};

const pickupIcon = createIcon('#28a745', '📍'); // Green with pin icon
const dropIcon = createIcon('#dc3545', '🏁');   // Red with flag icon
const driverIcon = createIcon('#007bff', '🚗'); // Blue with car icon

// Component to fit map bounds to show all markers
const FitBounds = ({ bounds }) => {
    const map = useMap();
    
    useEffect(() => {
        if (bounds && bounds.length > 0) {
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [bounds, map]);
    
    return null;
};

// Add these two helper functions near the existing constants/helpers:

/**
 * Helper function to clean the BASE_URL to the domain root for serving static files.
 */
const getStorageBaseUrl = () => {
    try {
        // Parse the full API URL (e.g., http://domain:8000/api)
        const url = new URL(BASE_URL);
        
        // Construct the base URL without the /api path segment
        let root = `${url.protocol}//${url.hostname}`;
        
        // If the port is explicitly defined and NOT standard HTTP/S, include it
        if (url.port && url.port !== '80' && url.port !== '443') {
            root += `:${url.port}`;
        }
        
        // Remove trailing slash if it exists
        if (root.endsWith('/')) {
            root = root.slice(0, -1);
        }

        return root;
    } catch (e) {
        console.error("Error parsing BASE_URL:", e);
        // Fallback safety if URL is malformed, but this may still result in a 404.
        return BASE_URL.replace(/\/api$/, '');
    }
};

// Create axios instance with Bearer token
const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Public Tracking Page Component
const PublicTrackingPage = () => {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);
    const [driverLocation, setDriverLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState('connecting'); // connecting, connected, error
    const socketRef = useRef(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(null);

    const fetchOrderDetails = async () => {
        setLoading(true);
        setError(null);
        
        try {
            console.log(`Fetching order details for ID: ${orderId}`);
            // Try multiple possible endpoints
            const endpoints = [
                `${BASE_URL}/orders/${orderId}/tracking`,
                `${BASE_URL}/orders/${orderId}`,
            ];
            
            let response = null;
            let lastError = null;
            
            for (const endpoint of endpoints) {
                try {
                    console.log(`Trying endpoint: ${endpoint}`);
                    response = await axios.get(endpoint);
                    if (response.data) {
                        console.log(`Success with endpoint: ${endpoint}`);
                        break;
                    }
                } catch (err) {
                    console.error(`Failed with endpoint ${endpoint}:`, err.message);
                    lastError = err;
                }
            }
            
            if (!response || !response.data) {
                throw lastError || new Error("No response from server");
            }
            
            console.log("Response data:", response.data);
            
            // Check the response structure
            let orderData;
            if (response.data.data) {
                orderData = response.data.data;
            } else if (response.data.order) {
                orderData = response.data.order;
            } else {
                orderData = response.data;
            }
            
            if (!orderData || !orderData.id) {
                throw new Error("Invalid order data received");
            }
            
            setOrder(orderData);
            setLastUpdated(new Date());
            
            // Try to establish WebSocket connection with multiple approaches
            tryWebSocketConnection();
            
            setLoading(false);
        } catch (err) {
            console.error("Error fetching order details:", err);
            let errorMessage = "Order not found or tracking link is invalid";
            
            if (err.response) {
                console.error("Error response data:", err.response.data);
                console.error("Error response status:", err.response.status);
                
                if (err.response.status === 404) {
                    errorMessage = "Order not found. Please check the tracking link.";
                } else if (err.response.status === 401 || err.response.status === 403) {
                    errorMessage = "You are not authorized to view this order.";
                } else if (err.response.data && err.response.data.message) {
                    errorMessage = err.response.data.message;
                }
            } else if (err.request) {
                errorMessage = "Network error. Please check your connection.";
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
            setLoading(false);
            setConnectionStatus('error');
        }
    };

    // Try different WebSocket connection approaches
    const tryWebSocketConnection = () => {
        // Clean up any existing connection
        if (socketRef.current) {
            socketRef.current.disconnect();
        }
        
        // Clear any existing polling interval
        if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
        }
        
        // Try multiple connection approaches with different paths
        tryConnectionWithPath('');
    };
    
    const tryConnectionWithPath = (path) => {
        try {
            // Create socket URL with the specified path
            const baseUrl = BASE_URL.replace(/^https?:\/\//, '');
            const socketUrl = `${baseUrl}${path}`;
            console.log(`Connecting to WebSocket at: ${socketUrl}`);
            
            socketRef.current = io(socketUrl, {
                transports: ['websocket'],
                timeout: 5000, // Shorter timeout for faster fallback
                reconnection: false, // Don't try to reconnect automatically, we'll handle it
                forceNew: true,
                query: {
                    EIO: 4,
                    order_id: orderId
                }
            });
            
            socketRef.current.on('connect', () => {
                console.log('Connected to WebSocket server');
                setConnectionStatus('connected');
                socketRef.current.emit('join_tracking', { order_id: orderId });
            });
            
            socketRef.current.on('connect_error', (err) => {
                console.error(`WebSocket connection error with path "${path}":`, err.message);
                
                // Try different paths
                if (path === '') {
                    tryConnectionWithPath('/socket.io');
                } else if (path === '/socket.io') {
                    tryConnectionWithPath('/api');
                } else if (path === '/api') {
                    tryConnectionWithPath('/api/socket.io');
                } else {
                    // If all paths fail, fall back to polling
                    console.log('All WebSocket paths failed, setting up polling');
                    setConnectionStatus('error');
                    setupPollingFallback();
                }
            });
            
            socketRef.current.on('driver_location_update', (data) => {
                if (data.order_id == orderId) {
                    setDriverLocation({
                        lat: parseFloat(data.lat),
                        lng: parseFloat(data.lng),
                        timestamp: data.timestamp
                    });
                    setLastUpdated(new Date());
                }
            });
            
            socketRef.current.on('disconnect', () => {
                console.log('Disconnected from WebSocket server');
                setConnectionStatus('error');
                setupPollingFallback();
            });
            
        } catch (err) {
            console.error(`Error creating WebSocket connection with path "${path}":`, err);
            
            // Try different paths
            if (path === '') {
                tryConnectionWithPath('/socket.io');
            } else if (path === '/socket.io') {
                tryConnectionWithPath('/api');
            } else if (path === '/api') {
                tryConnectionWithPath('/api/socket.io');
            } else {
                // If all paths fail, fall back to polling
                setConnectionStatus('error');
                setupPollingFallback();
            }
        }
    };
    
    // Set up polling as a fallback
    const setupPollingFallback = () => {
        console.log("Setting up polling fallback mechanism");
        
        // Clear any existing interval
        if (pollingInterval) {
            clearInterval(pollingInterval);
        }
        
        // Set up new polling interval
        const intervalId = setInterval(async () => {
            try {
                // Use the tracking endpoint to get the latest order data including driver location
                const endpoints = [
                    `${BASE_URL}/orders/${orderId}/tracking`,
                    `${BASE_URL}/api/orders/${orderId}/tracking`
                ];
                
                let response = null;
                let lastError = null;
                
                for (const endpoint of endpoints) {
                    try {
                        console.log(`Trying polling endpoint: ${endpoint}`);
                        response = await axios.get(endpoint);
                        if (response.data) {
                            console.log(`Polling success with endpoint: ${endpoint}`);
                            break;
                        }
                    } catch (err) {
                        console.error(`Polling failed with endpoint ${endpoint}:`, err.message);
                        lastError = err;
                    }
                }
                
                if (response && response.data) {
                    let orderData;
                    
                    // Handle different response formats
                    if (response.data.data) {
                        orderData = response.data.data;
                    } else if (response.data.order) {
                        orderData = response.data.order;
                    } else {
                        orderData = response.data;
                    }
                    
                    // Update order data if it has changed
                    if (orderData && orderData.id) {
                        setOrder(orderData);
                    }
                    
                    // Extract driver location if available
                    if (orderData && orderData.driver_location) {
                        const driverLoc = orderData.driver_location;
                        setDriverLocation({
                            lat: parseFloat(driverLoc.lat),
                            lng: parseFloat(driverLoc.lng),
                            timestamp: driverLoc.timestamp || new Date().toISOString()
                        });
                        setLastUpdated(new Date());
                    }
                }
            } catch (err) {
                console.error("Error polling driver location:", err);
            }
        }, 30000); // Poll every 30 seconds
        
        setPollingInterval(intervalId);
    };

    useEffect(() => {
        fetchOrderDetails();

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, [orderId]);

    const handleRetry = () => {
        setRetryCount(prev => prev + 1);
        fetchOrderDetails();
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3">Loading tracking information...</p>
                    <p className="text-muted small">Order ID: {orderId}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
                <div className="alert alert-danger text-center p-4" style={{ maxWidth: '500px' }}>
                    <h4 className="alert-heading"><FaExclamationTriangle className="me-2" />Tracking Error</h4>
                    <p className="mb-3">{error}</p>
                    <p className="text-muted small mb-3">Order ID: {orderId}</p>
                    <Button variant="primary" onClick={handleRetry} className="me-2">
                        <FaRedo className="me-1" /> Retry
                    </Button>
                    {retryCount >= 3 && (
                        <Button variant="outline-secondary" onClick={() => window.location.reload()}>
                            Refresh Page
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    // Calculate bounds for map to fit all markers
    const calculateBounds = () => {
        const bounds = [];
        
        if (order.pickup_lat && order.pickup_lng) {
            bounds.push([parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)]);
        }
        
        if (order.delivery_lat && order.delivery_lng) {
            bounds.push([parseFloat(order.delivery_lat), parseFloat(order.delivery_lng)]);
        }
        
        if (driverLocation) {
            bounds.push([driverLocation.lat, driverLocation.lng]);
        }
        
        return bounds.length > 0 ? bounds : null;
    };

    const bounds = calculateBounds();

    // Calculate order progress
    const calculateProgress = () => {
        if (!driverLocation) return 0;
        
        if (!order.pickup_lat || !order.pickup_lng || !order.delivery_lat || !order.delivery_lng) {
            return 0;
        }
        
        // Simple distance-based progress calculation
        const pickup = [parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)];
        const delivery = [parseFloat(order.delivery_lat), parseFloat(order.delivery_lng)];
        const driver = [driverLocation.lat, driverLocation.lng];
        
        // Calculate distances (simplified)
        const totalDistance = calculateDistance(pickup, delivery);
        const distanceToPickup = calculateDistance(driver, pickup);
        const distanceToDelivery = calculateDistance(driver, delivery);
        
        if (distanceToPickup < 0.5) { // If very close to pickup
            return 25; // 25% progress
        } else if (distanceToDelivery < 0.5) { // If very close to delivery
            return 100; // 100% progress
        } else {
            // Estimate progress based on distance covered
            const progress = Math.min(90, Math.max(25, (1 - distanceToDelivery / totalDistance) * 100));
            return Math.round(progress);
        }
    };
    
    // Helper function to calculate distance between two points
    const calculateDistance = (point1, point2) => {
        const R = 6371; // Radius of the earth in km
        const dLat = deg2rad(point2[0] - point1[0]);
        const dLon = deg2rad(point2[1] - point1[1]);
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(deg2rad(point1[0])) * Math.cos(deg2rad(point2[0])) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
        const d = R * c; // Distance in km
        return d;
    };
    
    const deg2rad = (deg) => {
        return deg * (Math.PI/180);
    };

    const progress = calculateProgress();

    return (
        <div className="container-fluid vh-100 d-flex flex-column p-0 bg-light">
            {/* Header */}
            <div className="bg-primary text-white p-3 shadow-sm">
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">Order Tracking</h4>
                        <div className="d-flex align-items-center">
                            {connectionStatus === 'connected' ? (
                                <Badge bg="success" className="me-2">
                                    <FaCheckCircle className="me-1" /> Live
                                </Badge>
                            ) : connectionStatus === 'error' ? (
                                <Badge bg="warning" className="me-2">
                                    <FaExclamationTriangle className="me-1" /> Limited Updates
                                </Badge>
                            ) : (
                                <Badge bg="info" className="me-2">
                                    Connecting...
                                </Badge>
                            )}
                            {lastUpdated && (
                                <small className="text-white-50">
                                    Updated: {lastUpdated.toLocaleTimeString()}
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Main Content */}
            <div className="container flex-grow-1 py-4">
                <Row className="g-4">
                    {/* Order Information Card */}
                    <Col lg={4} md={6}>
                        <Card className="h-100 shadow-sm">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">Order Information</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <h6 className="text-muted">Order #</h6>
                                    <p className="mb-0 fw-bold">{order.order_number || order.id}</p>
                                </div>
                                
                                <div className="mb-3">
                                    <h6 className="text-muted">Status</h6>
                                    <Badge 
                                        bg={
                                            order.status === 'delivered' ? 'success' :
                                            order.status === 'in_transit' ? 'primary' :
                                            order.status === 'picked_up' ? 'info' :
                                            order.status === 'cancelled' ? 'danger' : 'warning'
                                        }
                                    >
                                        {order.status?.replace('_', ' ').toUpperCase()}
                                    </Badge>
                                </div>
                                
                                <div className="mb-3">
                                    <h6 className="text-muted">Customer</h6>
                                    <p className="mb-0">{order.customer_name || 'N/A'}</p>
                                </div>
                                
                                <div>
                                    <h6 className="text-muted">Delivery Progress</h6>
                                    <ProgressBar now={progress} label={`${progress}%`} className="mb-2" />
                                    <small className="text-muted">
                                        {progress < 25 ? 'Preparing for pickup' :
                                         progress < 50 ? 'Picking up order' :
                                         progress < 75 ? 'In transit' :
                                         progress < 100 ? 'Approaching destination' : 'Delivered'}
                                    </small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    
                    {/* Driver Information Card */}
                    <Col lg={4} md={6}>
                        <Card className="h-100 shadow-sm">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">Driver Information</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <h6 className="text-muted">Name</h6>
                                    <p className="mb-0">{order.driver_name || 'N/A'}</p>
                                </div>
                                
                                <div className="mb-3">
                                    <h6 className="text-muted">Vehicle</h6>
                                    <p className="mb-0">{order.vehicle_details || 'N/A'}</p>
                                </div>
                                
                                <div>
                                    <h6 className="text-muted">Current Location</h6>
                                    {driverLocation ? (
                                        <p className="mb-0">
                                            <small>
                                                {driverLocation.lat.toFixed(6)}, {driverLocation.lng.toFixed(6)}
                                            </small>
                                        </p>
                                    ) : (
                                        <p className="mb-0 text-muted">Location not available</p>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    
                    {/* Route Information Card */}
                    <Col lg={4} md={12}>
                        <Card className="h-100 shadow-sm">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">Route Information</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <h6 className="text-muted text-success">Pickup Location</h6>
                                    <p className="mb-0">{order.pickup_address || 'N/A'}</p>
                                </div>
                                
                                <div>
                                    <h6 className="text-muted text-danger">Delivery Location</h6>
                                    <p className="mb-0">{order.delivery_address || 'N/A'}</p>
                                </div>
                                
                                <div className="mt-3 d-flex justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <div style={{ width: '15px', height: '15px', backgroundColor: '#28a745', borderRadius: '50%', marginRight: '8px' }}></div>
                                        <span>Pickup</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div style={{ width: '15px', height: '15px', backgroundColor: '#dc3545', borderRadius: '50%', marginRight: '8px' }}></div>
                                        <span>Delivery</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div style={{ width: '15px', height: '15px', backgroundColor: '#007bff', borderRadius: '50%', marginRight: '8px' }}></div>
                                        <span>Driver</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                
                {/* Map Section */}
                <Card className="mt-4 shadow-sm">
                    <Card.Header className="bg-white">
                        <h5 className="mb-0">Live Tracking Map</h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div style={{ height: '500px', width: '100%' }}>
                            {bounds ? (
                               <MapContainer center={bounds ? bounds[0] : [24.520, 54.286]} zoom={13} style={{ height: '100%', width: '100%' }}>
<TileLayer
    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
/>
                                    {/* Pickup marker */}
                                    <Marker 
                                        position={[parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)]} 
                                        icon={pickupIcon}
                                    >
                                        <Popup>
                                            <strong>Pickup Location</strong><br />
                                            {order.pickup_address}
                                        </Popup>
                                    </Marker>
                                    
                                    {/* Drop marker */}
                                    <Marker 
                                        position={[parseFloat(order.delivery_lat), parseFloat(order.delivery_lng)]} 
                                        icon={dropIcon}
                                    >
                                        <Popup>
                                            <strong>Delivery Location</strong><br />
                                            {order.delivery_address}
                                        </Popup>
                                    </Marker>
                                    
                                    {/* Driver marker */}
                                    {driverLocation && (
                                        <Marker 
                                            position={[driverLocation.lat, driverLocation.lng]} 
                                            icon={driverIcon}
                                        >
                                            <Popup>
                                                <strong>Driver Location</strong><br />
                                                Last updated: {new Date(driverLocation.timestamp).toLocaleString()}
                                            </Popup>
                                        </Marker>
                                    )}
                                    
                                    {/* Route line */}
                                    <Polyline 
                                        positions={bounds} 
                                        color="#007bff" 
                                        weight={4} 
                                        opacity={0.7}
                                    />
                                    
                                    {/* Fit bounds to show all markers */}
                                    <FitBounds bounds={bounds} />
                                </MapContainer>
                            ) : (
                                <div className="d-flex justify-content-center align-items-center bg-light" style={{ height: '100%' }}>
                                    <Alert variant="warning" className="text-center">
                                        <FaExclamationTriangle className="me-2" />
                                        No location data available
                                    </Alert>
                                </div>
                            )}
                        </div>
                    </Card.Body>
                </Card>
                
                {/* Footer with update info */}
                <div className="text-center mt-3 text-muted">
                    <small>
                        {driverLocation ? 
                            `Last updated: ${new Date(driverLocation.timestamp).toLocaleString()}` : 
                            'Waiting for driver location...'}
                    </small>
                </div>
            </div>
        </div>
    );
};

// Rest of the SalesForm component remains the same until the PublicTrackingPage component

// AutocompleteDropdown Component
const AutocompleteDropdown = ({ 
    options = [], 
    value, 
    onChange, 
    placeholder = "Select an option", 
    name,
    displayField = "name",
    valueField = "id",
    className = "",
    required = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option => {
                const displayValue = option[displayField] || option.number || option.id || "";
                return displayValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
            });
            setFilteredOptions(filtered);
        }
    }, [searchTerm, options, displayField]);

    useEffect(() => {
        if (value) {
            const selectedOption = options.find(option => option[valueField] == value);
            if (selectedOption) {
                const displayValue = selectedOption[displayField] || selectedOption.number || selectedOption.id || "";
                setSearchTerm(displayValue.toString());
            }
        } else {
            setSearchTerm("");
        }
    }, [value, options, displayField, valueField]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        setIsOpen(true);
        if (newSearchTerm === "") {
            onChange({ target: { name, value: "" } });
        }
    };

    const handleOptionSelect = (option) => {
        const displayValue = option[displayField] || option.number || option.id || "";
        setSearchTerm(displayValue.toString());
        onChange({ target: { name, value: option[valueField] } });
        setIsOpen(false);
    };

    const handleInputFocus = () => setIsOpen(true);

    const handleKeyDown = (e) => {
        if (e.key === "Escape") setIsOpen(false);
        else if (e.key === "ArrowDown") {
            e.preventDefault();
            setIsOpen(true);
        }
    };

    return (
        <div ref={dropdownRef} className={`autocomplete-dropdown ${className}`} style={{ position: "relative" }}>
            <Form.Control
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                required={required}
                autoComplete="off"
            />
            {isOpen && (
                <div
                    className="autocomplete-options"
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderTop: "none",
                        borderRadius: "0 0 0.375rem 0.375rem",
                        maxHeight: "200px",
                        overflowY: "auto",
                        zIndex: 1000,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                    }}
                >
                    {filteredOptions.length > 0 ? (
                        <>
                            <div
                                style={{
                                    padding: "4px 12px",
                                    fontSize: "12px",
                                    color: "#6c757d",
                                    backgroundColor: "#f8f9fa",
                                    borderBottom: "1px solid #e9ecef"
                                }}
                            >
                                Showing {filteredOptions.length} of {options.length} options
                            </div>
                            {filteredOptions.map((option) => (
                                <div
                                    key={option[valueField]}
                                    className="autocomplete-option"
                                    onClick={() => handleOptionSelect(option)}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0",
                                        backgroundColor: option[valueField] == value ? "#e3f2fd" : "transparent"
                                    }}
                                    onMouseEnter={(e) => { e.target.style.backgroundColor = "#f5f5f5"; }}
                                    onMouseLeave={(e) => { e.target.style.backgroundColor = option[valueField] == value ? "#e3f2fd" : "transparent"; }}
                                >
                                    {option[displayField] || option.number || option.id || ""}
                                </div>
                            ))}
                        </>
                    ) : (
                        <div
                            style={{
                                padding: "8px 12px",
                                color: "#6c757d",
                                fontStyle: "italic"
                            }}
                        >
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Create axios instance with Bearer token
//const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Upload attachment function
// Around Line 452 (Replace the existing uploadAttachment function)
const uploadAttachment = async (orderId, file, userId, orderStatus) => { // Added orderStatus parameter
    if (!file) {
        console.warn("No file selected, skipping attachment upload.");
        return null;
    }
    const attachableId = Number(orderId);
    if (isNaN(attachableId)) {
        console.error("Invalid orderId for attachment:", orderId);
        return null;
    }

    const formDataToSend = new FormData();
    
    // FIX: Map the order status to the required attachable_type API string
    const attachableTypeMap = {
        'pending': 'order_pending',
        'assigned': 'order_pending', 
        'picked_up': 'order_picked_up',
        'in_transit': 'order_in_transit',
        'delivered': 'order_delivered',
        'returned': 'order_returned',
        'rejected': 'order_rejected',
        'completed': 'order_delivered', 
    };
    
    const apiAttachableType = attachableTypeMap[orderStatus?.toLowerCase()] || 'order_pending';
    
    formDataToSend.append("attachable_type", apiAttachableType);
    formDataToSend.append("attachable_id", attachableId);
    formDataToSend.append("uploaded_by_id", userId);

    let type = "order_document";
    if (file.type.startsWith("image/")) type = "pod_photo";
    else if (file.type.startsWith("audio/")) type = "voice_note";
    
    formDataToSend.append("type", type);
    formDataToSend.append("file", file);

    try {
        const res = await authAxios.post("/attachments", formDataToSend, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        console.log("✅ Attachment uploaded:", res.data);
        return res.data;
    } catch (err) {
        console.error("Attachment upload failed:", err.response?.data || err);
        return null;
    }
};

// Geocoding Service Function with caching
const locationCache = new Map();
const getAddressFromCoordinates = async (lat, lng) => {
    const cacheKey = `${lat},${lng}`;
    
    // Return cached result if available
    if (locationCache.has(cacheKey)) {
        return locationCache.get(cacheKey);
    }
    
    try {
        const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`);
        const address = response.data.display_name || "Location not found";
        
        // Cache the result
        locationCache.set(cacheKey, address);
        
        return address;
    } catch (error) {
        console.error("Error during reverse geocoding:", error);
        return "Location not found";
    }
};

// Draggable Marker Component
const DraggableMarker = ({ onDragEnd, initialPosition }) => {
    const [position, setPosition] = useState(initialPosition);
    const markerRef = useRef(null);
    const eventHandlers = useMemo(() => ({
        dragend() {
            const marker = markerRef.current;
            if (marker) {
                const newPos = marker.getLatLng();
                setPosition([newPos.lat, newPos.lng]);
                onDragEnd(newPos);
            }
        },
    }), [onDragEnd]);

    useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
            onDragEnd(e.latlng);
        },
    });

    useEffect(() => { setPosition(initialPosition); }, [initialPosition]);

    return <Marker draggable={true} eventHandlers={eventHandlers} position={position} ref={markerRef}></Marker>;
};

// MapPopup Component with coordinate input
const MapPopup = ({ show, handleClose, title, selectedLocation, setSelectedLocation, setAddress }) => {
    const defaultPosition = [24.520, 54.286]; // Coordinates for the UAE (Abu Dhabi)
    const position = selectedLocation || defaultPosition;
    const [manualLat, setManualLat] = useState(position[0]);
    const [manualLng, setManualLng] = useState(position[1]);

    useEffect(() => {
        if (selectedLocation) {
            setManualLat(selectedLocation[0]);
            setManualLng(selectedLocation[1]);
        } else {
            setManualLat(defaultPosition[0]);
            setManualLng(defaultPosition[1]);
        }
    }, [selectedLocation, defaultPosition]);

    const handleDragEnd = (newLocation) => {
        setSelectedLocation([newLocation.lat, newLocation.lng]);
        setManualLat(newLocation.lat);
        setManualLng(newLocation.lng);
    };

    const handleManualLatChange = (e) => {
        const lat = parseFloat(e.target.value);
        if (!isNaN(lat)) {
            setManualLat(lat);
            setSelectedLocation([lat, manualLng]);
        }
    };

    const handleManualLngChange = (e) => {
        const lng = parseFloat(e.target.value);
        if (!isNaN(lng)) {
            setManualLng(lng);
            setSelectedLocation([manualLat, lng]);
        }
    };

    const handleSelectClick = async () => {
        const address = await getAddressFromCoordinates(manualLat, manualLng);
        setAddress([manualLat, manualLng], address);
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{ height: '400px', width: '100%' }}>
<MapContainer center={position} zoom={10} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <DraggableMarker onDragEnd={handleDragEnd} initialPosition={position} />
                    </MapContainer>
                </div>
                
                <Row className="mt-3">
                   {/*  <Col md={6}>
                        <Form.Group>
                            <Form.Label>Latitude</Form.Label>
                            <Form.Control
                                type="number"
                                step="any"
                                value={manualLat}
                                onChange={handleManualLatChange}
                            />
                        </Form.Group>
                    </Col>*/}
                   {/* <Col md={6}>
                        <Form.Group>
                            <Form.Label>Longitude</Form.Label>
                            <Form.Control
                                type="number"
                                step="any"
                                value={manualLng}
                                onChange={handleManualLngChange}
                            />
                        </Form.Group>
                    </Col>*/}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="primary" onClick={handleSelectClick}>Select Location</Button>
            </Modal.Footer>
        </Modal>
    );
};

// Initial form data
const initialFormData = {
    po: "", document: null, vendor: "", customer: "",
    fromLocation: "", dropLocationDetails: "", remarks: "",
    salesReturn: "", returnValue: "", toLocation: "",
    poId: "", invoiceNumber: "",
    fromLatitude: "", fromLongitude: "",
    dropLatitude: "", dropLongitude: "",
    toLatitude: "", toLongitude: "",
};

const SalesForm = () => {
    const navigate = useNavigate(); // Add this for navigation
    
    // Helper function for status badge
    const getStatusBadge = (status) => {
        const statusColors = {
            pending: 'warning',
            completed: 'success',
            cancelled: 'danger',
            in_progress: 'info',
            delivered: 'success',
            picked_up: 'primary',
            assigned: 'info',
            in_transit: 'primary',
            rejected: 'danger',
            returned: 'warning'
        };
        
        return (
            <Badge bg={statusColors[status] || 'secondary'}>
                {status?.replace('_', ' ').toUpperCase()}
            </Badge>
        );
    };

    // Helper function for type label
    const getTypeLabel = (type) => {
        const typeLabels = {
            purchase: 'Purchase Order',
            sales: 'Sales Order',
            sales_return: 'Sales Return',
            purchase_return: 'Purchase Return',
            purchase_order: 'Purchase Order',
            sales_order: 'Sales Order',
            payment_collection: 'Payment Collection'
        };
        return typeLabels[type] || 'Unknown';
    };

    // Logout handler
    const handleLogout = () => {
        localStorage.removeItem("authToken");
        navigate("/");
    };

    const [vendors, setVendors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [showroomses, setShowroomses] = useState([]);
    const [poNumbers, setPoNumbers] = useState([]);
    const [activeForm, setActiveForm] = useState(null);
    const [activeView, setActiveView] = useState('form');
    const [salesHistory, setSalesHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [showCustomerForm, setShowCustomerForm] = useState(false);
    const [showVendorForm, setShowVendorForm] = useState(false);
    const [showOrderTrack, setShowOrderTrack] = useState(false);

    const [showFromMap, setShowFromMap] = useState(false);
    const [showDropMap, setShowDropMap] = useState(false);
    const [showToMap, setShowToMap] = useState(false);

    const [fromCoords, setFromCoords] = useState(null);
    const [dropCoords, setDropCoords] = useState(null);
    const [toCoords, setToCoords] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [customerOrders, setCustomerOrders] = useState([]);
    const [selectedOrderId, setSelectedOrderId] = useState("");

    // State for the message modal
    const [messageModal, setMessageModal] = useState({
        show: false,
        title: "",
        body: ""
    });

    // State for order details modal
    const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [currentAttachment, setCurrentAttachment] = useState(null);
    
    // Tracking modal state - exactly like AdminDashboard
    const [showTrackModal, setShowTrackModal] = useState(false);
    const [trackDriverId, setTrackDriverId] = useState(null);
    const [trackOrder, setTrackOrder] = useState(null);
    
    // State for user ID
    const [userId, setUserId] = useState(null);

    const showMessageModal = (title, body) => {
        setMessageModal({ show: true, title, body });
    };

    const handleCloseMessageModal = () => {
        setMessageModal({ show: false, title: "", body: "" });
    };

    // ... after state declarations like [activeView, setActiveView] = useState('form');

const handleViewChange = (viewName) => {
    // 1. Set the main view mode: 'form', 'history', 'customers', 'vendors', or 'track'
    setActiveView(viewName.startsWith('form') ? 'form' : viewName); 
    
    // 2. Set the specific form mode, if applicable
    if (viewName.startsWith('form')) {
        setActiveForm(viewName); // Sets to 'formA', 'formB', or 'formC'
    } else {
        setActiveForm(null);
    }
    
    // 3. Trigger history fetch only when explicitly navigating to the history view
    if (viewName === 'history') {
        fetchSalesHistory();
    }
    
    // 4. Reset form data for a clean slate when leaving the forms
    if (!viewName.startsWith('form')) {
        setFormData(initialFormData);
        setSelectedOrderId("");
        setFromCoords(null);
        setDropCoords(null);
        setToCoords(null);
    }
};
    // Fetch all pages helper
    const fetchAllPages = async (endpoint, pageParam = 'page', perPage = 100) => {
        let allData = [], currentPage = 1, hasMorePages = true;
        while (hasMorePages) {
            try {
                const response = await authAxios.get(`${endpoint}?${pageParam}=${currentPage}&per_page=${perPage}`);
                const pageData = response.data?.data?.data || response.data?.data || response.data || [];
                if (Array.isArray(pageData) && pageData.length > 0) {
                    allData = [...allData, ...pageData];
                    currentPage++;
                    if (pageData.length < perPage) hasMorePages = false;
                } else { hasMorePages = false; }
            } catch (error) { console.error(`Error fetching ${endpoint} page ${currentPage}:`, error); hasMorePages = false; }
        }
        return allData;
    };

    // Auto-fill Sales Return fields when customer changes
    useEffect(() => {
        if (!formData.customer) {
            setCustomerOrders([]); 
            return;
        }

        const fetchCustomerOrders = async () => {
            try {
                const res = await fetchAllPages(`/orders`);
                const ordersList = res || [];

                const customerId = parseInt(formData.customer, 10);
                const filtered = ordersList.filter(order => 
                    order.customer?.id === customerId
                );

                setCustomerOrders(filtered);
            } catch (err) {
                console.error("❌ Error fetching customer orders:", err);
            }
        };

        fetchCustomerOrders();
    }, [formData.customer]);

    useEffect(() => {
        if (!selectedOrderId) return;

        const orderId = parseInt(selectedOrderId, 10);
        const orderData = customerOrders.find(o => o.id === orderId);
        if (!orderData) return;

        console.log("✅ Selected Order Data:", orderData);

        setFormData(prev => ({
            ...prev,
            po: orderData.po_number || "",
            invoiceNumber: orderData.invoice_number || "",
            fromLocation: orderData.pickup_address || orderData.warehouse?.name || "",
            toLocation: orderData.delivery_address || orderData.showroom?.name || "",
            dropLocationDetails: orderData.drop_location || orderData.delivery_address || "",
            returnValue: orderData.return_value || "0",
            codAmount: orderData.cod_amount || "0",
            currency: orderData.currency || "AED",
            orderStatus: orderData.status || "pending",
            fromLatitude: orderData.pickup_lat || "",
            fromLongitude: orderData.pickup_lng || "",
            dropLatitude: orderData.drop_lat || "",
            dropLongitude: orderData.drop_lng || "",
            toLatitude: orderData.delivery_lat || "",
            toLongitude: orderData.delivery_lng || "",
        }));

        if (orderData.pickup_lat && orderData.pickup_lng) {
            setFromCoords([orderData.pickup_lat, orderData.pickup_lng]);
        }
        if (orderData.delivery_lat && orderData.delivery_lng) {
            setToCoords([orderData.delivery_lat, orderData.delivery_lng]);
        }
        if (orderData.drop_lat && orderData.drop_lng) {
            setDropCoords([orderData.drop_lat, orderData.drop_lng]);
        }
    }, [selectedOrderId, customerOrders]);

    // Fetch initial data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [vendorsData, showroomsesData, poData] = await Promise.all([
                    fetchAllPages('/vendors'),
                    fetchAllPages('/showrooms'),
                    fetchAllPages('/purchase-orders')
                ]);

                const ordersData = await fetchAllPages('/orders');
                const uniqueCustomersMap = {};
                ordersData.forEach(order => { 
                    if (order.customer && !uniqueCustomersMap[order.customer.id]) {
                        uniqueCustomersMap[order.customer.id] = order.customer;
                    } 
                });
                const customersData = Object.values(uniqueCustomersMap);

                setVendors(vendorsData);
                setShowroomses(showroomsesData);
                setPoNumbers(poData);
                setCustomers(customersData);
            } catch (err) { console.error("Error fetching data:", err); }
        };
        fetchData();

        // Fetch user ID
        const fetchUserId = async () => {
            try {
                const res = await authAxios.get('/user');
                setUserId(res.data.id);
            } catch (err) {
                console.error("Error fetching user ID:", err);
            }
        };
        fetchUserId();
    }, []);

// Fetch sales history
const fetchSalesHistory = async () => {
    setLoadingHistory(true);
    try {
        const userId = localStorage.getItem('userId');
        const response = await authAxios.get(`/orders?created_by=${userId}`); // ✅ Correctly filter by user
        console.log("Sales history raw response:", response.data);

        let orders = [];
        if (response.data && response.data.data) {
            if (Array.isArray(response.data.data)) {
                orders = response.data.data;
            } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
                orders = response.data.data.data;
            }
        } else if (Array.isArray(response.data)) {
            orders = response.data;
        }

        console.log("Orders array before processing:", orders);

        // Process orders to include structured attachments
        const processedOrders = orders.map(order => {
            console.log(`Debug: Order ${order.id} attachments raw:`, order.attachments);

            const attachments = Array.isArray(order.attachments?.data ? order.attachments.data : order.attachments)
                ? (order.attachments?.data || order.attachments)
                : [];

            console.log(`Debug: Order ${order.id} attachments array:`, attachments);

            const pod_photos = attachments
                .filter(a => a.type?.toLowerCase() === "pod_photo")
                .map(a => ({ ...a, url: a.path ? `${BASE_URL}/${a.path}` : null }));

            const voice_notes = attachments
                .filter(a => a.type?.toLowerCase() === "voice_note")
                .map(a => ({ ...a, url: a.path ? `${BASE_URL}/${a.path}` : null }));

            const order_docs = attachments
                .filter(a => a.type?.toLowerCase() === "order_document")
                .map(a => ({ ...a, url: a.path ? `${BASE_URL}/${a.path}` : null }));

            return {
                ...order,
                customer_name: order.customer?.name || "",
                vendor_name: order.vendor?.name || "",
                driver_name: order.driver?.name || "Unassigned",
                pickup_address: order.pickup_address || "N/A",
                drop_address: order.delivery_address || "N/A",
                status: order.status || "pending",
                order_number: order.po_number || order.id,
                type: order.type || "N/A",
                attachments: {
                    pod_photos,
                    voice_notes,
                    order_docs
                }
            };
        });

        console.log("Processed orders with structured attachments:", processedOrders);
        setSalesHistory(processedOrders);

    } catch (error) {
        console.error("Error fetching sales history:", error);
        showMessageModal("Error", "Failed to fetch sales history");
        setSalesHistory([]);
    } finally {
        setLoadingHistory(false);
    }
};

    // Tracking handler - exactly like AdminDashboard
    const handleTrackDriver = (order) => {
        setTrackDriverId(order.driver_id || null);
        setTrackOrder(order);
        setShowTrackModal(true);
    };

    // Close handler - exactly like AdminDashboard
    const handleCloseTrackModal = () => {
        setShowTrackModal(false);
        setTrackDriverId(null);
        setTrackOrder(null);
    };

    // Fetch history when switching to history view
    useEffect(() => {
        if (activeView === 'history') {
            fetchSalesHistory();
        }
    }, [activeView]);

    // Ensure body can scroll when SalesForm is mounted
    useEffect(() => {
        // Override AdminLayout.css which sets overflow: hidden
        const originalBodyOverflow = document.body.style.overflow;
        const originalBodyHeight = document.body.style.height;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const originalHtmlHeight = document.documentElement.style.height;
        const rootElement = document.getElementById('root');
        const originalRootOverflow = rootElement?.style.overflow;
        const originalRootHeight = rootElement?.style.height;

        // Force enable scrolling
        document.body.style.overflow = 'auto';
        document.body.style.height = 'auto';
        document.body.style.maxHeight = 'none';
        document.documentElement.style.overflow = 'auto';
        document.documentElement.style.height = 'auto';
        document.documentElement.style.maxHeight = 'none';
        
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
            rootElement.style.maxHeight = 'none';
        }

        return () => {
            // Reset on unmount
            document.body.style.overflow = originalBodyOverflow;
            document.body.style.height = originalBodyHeight;
            document.body.style.maxHeight = '';
            document.documentElement.style.overflow = originalHtmlOverflow;
            document.documentElement.style.height = originalHtmlHeight;
            document.documentElement.style.maxHeight = '';
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow || '';
                rootElement.style.height = originalRootHeight || '';
                rootElement.style.maxHeight = '';
            }
        };
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === "document" ? files[0] : value }));
    };

    const handleFromMapSelect = (coords, address) => { 
        setFromCoords(coords); 
        setFormData(prev => ({ 
            ...prev, 
            fromLocation: address,
            fromLatitude: coords[0],
            fromLongitude: coords[1]
        })); 
        setShowFromMap(false); 
    };
    
    const handleDropMapSelect = (coords, address) => { 
        setDropCoords(coords); 
        setFormData(prev => ({ 
            ...prev, 
            dropLocationDetails: address,
            dropLatitude: coords[0],
            dropLongitude: coords[1]
        })); 
        setShowDropMap(false); 
    };
    
    const handleToMapSelect = (coords, address) => { 
        setToCoords(coords); 
        setFormData(prev => ({ 
            ...prev, 
            toLocation: address,
            toLatitude: coords[0],
            toLongitude: coords[1]
        })); 
        setShowToMap(false); 
    };

    // Handle PO selection
    const handlePoSelect = async (e) => {
        const selectedPoId = e.target.value;
        setFormData(prev => ({ ...prev, poId: selectedPoId }));

        if (selectedPoId) {
            try {
                const poDetailsRes = await authAxios.get(`/purchase-orders/${selectedPoId}`);
                const poData = poDetailsRes.data?.data;
                if (poData) {
                    setFormData(prev => ({
                        ...prev,
                        vendor: poData.vendor?.id || "",
                        fromLocation: poData.vendor?.address || poData.pickup_address || "",
                        toLocation: poData.showroom?.address || poData.delivery_address || "",
                        fromLatitude: poData.vendor?.lat || "",
                        fromLongitude: poData.vendor?.lng || "",
                        toLatitude: poData.showroom?.lat || "",
                        toLongitude: poData.showroom?.lng || "",
                        invoiceNumber: poData.invoice_number || "",
                        returnValue: poData.return_value || "",
                    }));
                    if (poData.vendor?.lat && poData.vendor?.lng) setFromCoords([poData.vendor.lat, poData.vendor.lng]);
                    if (poData.showroom?.lat && poData.showroom?.lng) setToCoords([poData.showroom.lat, poData.showroom.lng]);
                }
            } catch (err) { 
                console.error("Error fetching PO details:", err); 
                showMessageModal("Error", "Error fetching PO details."); 
            }
        } else { 
            setFormData(prev => ({ 
                ...prev, 
                vendor: "", 
                fromLocation: "", 
                toLocation: "", 
                fromLatitude: "",
                fromLongitude: "",
                toLatitude: "",
                toLongitude: "",
                invoiceNumber: "", 
                returnValue: "" 
            })); 
            setFromCoords(null); 
            setToCoords(null); 
        }
    };

    // Handle PO selection for Purchase Return
    const handlePurchasePoSelect = async (e) => {
        const selectedPoId = e.target.value;
        setFormData(prev => ({ ...prev, poId: selectedPoId }));

        if (selectedPoId) {
            try {
                const poDetailsRes = await authAxios.get(`/purchase-orders/${selectedPoId}`);
                const poData = poDetailsRes.data?.data;
                console.log("📦 Full PO Data (from API):", poData);

                if (poData) {
                    const mappedData = {
                        fromLocation: poData.delivery_address || poData.showroom?.address || "",
                        toLocation: poData.pickup_address || poData.vendor?.address || "",
                        showroom: poData.showroom?.id || "",
                        fromLatitude: poData.showroom?.lat || "",
                        fromLongitude: poData.showroom?.lng || "",
                        toLatitude: poData.vendor?.lat || "",
                        toLongitude: poData.vendor?.lng || "",
                        invoiceNumber: poData.invoice_number || "",
                        returnValue: poData.return_value || "",
                    };

                    console.log("✅ Mapped Data for Purchase Return:", {
                        ...mappedData,
                        vendor: formData.vendor,
                    });

                    setFormData(prev => ({
                        ...prev,
                        ...mappedData,
                    }));

                    if (poData.showroom?.lat && poData.showroom?.lng) {
                        console.log("📍 FromCoords (Showroom/Delivery):", [poData.showroom.lat, poData.showroom.lng]);
                        setFromCoords([poData.showroom.lat, poData.showroom.lng]);
                    }
                    if (poData.vendor?.lat && poData.vendor?.lng) {
                        console.log("📍 ToCoords (Vendor/Pickup):", [poData.vendor.lat, poData.vendor.lng]);
                        setToCoords([poData.vendor.lat, poData.vendor.lng]);
                    }
                }
            } catch (err) {
                console.error("❌ Error fetching PO details:", err);
                showMessageModal("Error", "Error fetching PO details.");
            }
        } else {
            console.log("⚠️ No PO selected. Resetting form.");
            setFormData(prev => ({
                ...prev,
                vendor: "",
                showroom: "",
                fromLocation: "",
                toLocation: "",
                fromLatitude: "",
                fromLongitude: "",
                toLatitude: "",
                toLongitude: "",
                invoiceNumber: "",
                returnValue: ""
            }));
            setFromCoords(null);
            setToCoords(null);
        }
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let orderRes;
            const basePayload = {
                pickup_address: formData.fromLocation,
                delivery_address: activeForm === "formA" ? formData.dropLocationDetails : formData.toLocation,
                notes: formData.remarks,
                pickup_lat: formData.fromLatitude ? parseFloat(formData.fromLatitude) : (fromCoords ? fromCoords[0] : null),
                pickup_lng: formData.fromLongitude ? parseFloat(formData.fromLongitude) : (fromCoords ? fromCoords[1] : null),
                delivery_lat: (activeForm === "formA" ? formData.dropLatitude : formData.toLatitude) ? 
                    parseFloat(activeForm === "formA" ? formData.dropLatitude : formData.toLatitude) : 
                    ((activeForm === "formA" ? dropCoords : toCoords) ? (activeForm === "formA" ? dropCoords[0] : toCoords[0]) : null),
                delivery_lng: (activeForm === "formA" ? formData.dropLongitude : formData.toLongitude) ? 
                    parseFloat(activeForm === "formA" ? formData.dropLongitude : formData.toLongitude) : 
                    ((activeForm === "formA" ? dropCoords : toCoords) ? (activeForm === "formA" ? dropCoords[1] : toCoords[1]) : null),
            };

            if (activeForm === "formA") {
                orderRes = await authAxios.post("/orders/purchase", {
                    ...basePayload,
                    vendor_id: formData.vendor,
                    customer_id: formData.customer,
                    po_number: formData.po,
                    showroom_id: formData.toLocation,
                });
            } else if (activeForm === "formB") {
                const payload = { 
                    customer_id: formData.customer, 
                    order_id: selectedOrderId,  
                    invoice_number: formData.salesReturn || "", 
                    pickup_address: formData.fromLocation || "",  
                    delivery_address: formData.dropLocationDetails || "", 
                    showroom_id: formData.toLocation,
                    return_value: parseFloat(formData.returnValue) || 0, 
                    remarks: formData.remarks || "",
                    pickup_lat: formData.fromLatitude ? parseFloat(formData.fromLatitude) : (fromCoords ? fromCoords[0] : null),
                    pickup_lng: formData.fromLongitude ? parseFloat(formData.fromLongitude) : (fromCoords ? fromCoords[1] : null),
                    delivery_lat: formData.dropLatitude ? parseFloat(formData.dropLatitude) : (dropCoords ? dropCoords[0] : null),
                    delivery_lng: formData.dropLongitude ? parseFloat(formData.dropLongitude) : (dropCoords ? dropCoords[1] : null),
                };

                console.log("📦 Sales Return Payload:", payload);
                console.log("🏭 Selected Showroom ID:", formData.toLocation);

                orderRes = await authAxios.post("/orders/sales-return", payload);
            } else if (activeForm === "formC") {
                const payload = { 
                    ...basePayload,
                    vendor_id: formData.vendor,
                    po_number: formData.poId,
                    return_value: formData.returnValue || 0,
                    invoice_number: formData.invoiceNumber,
                    pickup_address: formData.toLocation || "",
                    delivery_address: formData.fromLocation || "",
                    showroom_id: formData.fromLocations,
                };

                orderRes = await authAxios.post("/orders/purchase-return", payload);
            }

           console.log("Order creation response:", JSON.stringify(orderRes.data, null, 2));
           const orderId = orderRes.data?.data?.id || orderRes.data?.id || orderRes.data?.order?.id;
           console.log("Extracted orderId:", orderId);


            if (formData.document && userId) {
                await uploadAttachment(orderId, formData.document, userId, "pending"); 
            }
            showMessageModal("Success", "Order & attachment created successfully!");

            setFormData(initialFormData);
            setFromCoords(null);
            setDropCoords(null);
            setToCoords(null);

        } catch (err) { 
            console.error("Submit failed:", err.response?.data || err); 
            showMessageModal("Error", "Submit failed!"); 
        }
    };

    // Function to handle opening order details
    const handleViewOrderDetails = (order) => {
        setSelectedOrder(order);
        setShowOrderDetailsModal(true);
    };

    // Function to handle opening attachment
   const handleOpenAttachment = (file) => {
    console.log("Attachment clicked:", file);

    if (!file.path) {
        showMessageModal("Error", "File path missing. Cannot display file.");
        return;
    }

    const storageRoot = getStorageBaseUrl();
    // file.path contains something like "attachments/..." or "storage/attachments/..."
    const publicStoragePath = file.path.startsWith('storage/') ? file.path : `storage/${file.path}`;

    const fileWithUrl = {
        ...file,
        // FIX 1: Corrected URL construction using the storage root
        url: `${storageRoot}/${publicStoragePath}` 
    };

    setCurrentAttachment(fileWithUrl);
    setShowAttachmentModal(true);
};

    // Sales History Table Component
    const SalesHistoryTable = () => {
        return (
            <div className="container mt-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3>Sales History</h3>
                    <Button variant="primary" onClick={() => setActiveView('form')}>
                        <i className="fa fa-arrow-left me-2"></i>Back to Forms
                    </Button>
                </div>
                
                {loadingHistory ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2">Loading sales history...</p>
                    </div>
                ) : !Array.isArray(salesHistory) || salesHistory.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="text-muted" style={{ fontSize: "18px" }}>
                            No data available
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead className="table-dark">
                                <tr>
                                    <th>Order ID</th>
                                    <th>Type</th>
                                    <th>Customer/Vendor</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Attachments</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {salesHistory.map((order) => (
                                    <tr key={order.id}>
                                        <td>#{order.id}</td>
                                        <td>{getTypeLabel(order.type)}</td>
                                        <td>
                                            {order.customer_name || order.vendor_name || 'N/A'}
                                        </td>
                                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                        <td>{getStatusBadge(order.status)}</td>
                                        <td>
                                            {order.currency || 'AED'} {order.total_amount || order.return_value || '0.00'}
                                        </td>
                                        <td>
                                            {order.attachments &&
                                                [
                                                    ...order.attachments.pod_photos,
                                                    ...order.attachments.voice_notes,
                                                    ...order.attachments.order_docs
                                                ].filter(file => file.url).length > 0 ? (
                                                [
                                                    ...order.attachments.pod_photos,
                                                    ...order.attachments.voice_notes,
                                                    ...order.attachments.order_docs
                                                ].filter(file => file.url)
                                                    .map((file, idx) => (
                                                        <Button
                                                            key={idx}
                                                            variant="outline-info"
                                                            size="sm"
                                                            className="me-1"
                                                            onClick={() => handleOpenAttachment(file)}
                                                        >
                                                            <i className="fa fa-paperclip"></i>
                                                            <FaBoxOpen />
                                                        </Button>
                                                    ))
                                            ) : (
                                                <span className="text-muted">No files</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => handleViewOrderDetails(order)}
                                                >
                                                    <i className="fa fa-eye"></i> View
                                                </Button>
                                                {order.driver_id && (
                                                    <Button 
                                                        variant="outline-success" 
                                                        size="sm"
                                                        onClick={() => handleTrackDriver(order)}
                                                    >
                                                        <i className="fa fa-truck"></i> Track
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ background: "rgb(0 24 127)" }}>
            {/* Message Modal */}
            <Modal
                show={messageModal.show}
                onHide={handleCloseMessageModal}
                centered
                size="md"
                className="text-center"
            >
                <Modal.Body
                    className="py-4"
                    style={{
                        backgroundColor: "#ffffff",
                        color: "#1f1f1f",
                        borderRadius: "15px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                    }}
                >
                    <div className="mb-3">
                        {messageModal.title === "Success" ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="green" className="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08 0l3.992-3.992a.75.75 0 0 0-1.08-1.04L7.5 9.439 5.53 7.47a.75.75 0 0 0-1.08 1.04l2.52 2.52z"/>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="red" className="bi bi-x-circle-fill" viewBox="0 0 16 16">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM4.646 4.646a.5.5 0 0 0 0 .708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646a.5.5 0 0 0-.708 0z"/>
                            </svg>
                        )}
                    </div>
                    <h5 className="mb-2">{messageModal.title}</h5>
                    <p>{messageModal.body}</p>
                    <Button
                        variant={messageModal.title === "Success" ? "success" : "danger"}
                        style={{
                            width: "100%",
                            fontWeight: "600",
                            borderRadius: "10px",
                            background: messageModal.title === "Success"
                                ? "linear-gradient(135deg, #28a745, #198754)"
                                : "linear-gradient(135deg, #dc3545, #b02a37)",
                            border: "none",
                            boxShadow: "0px 4px 10px rgba(0,0,0,0.25)"
                        }}
                        onClick={handleCloseMessageModal}
                    >
                        Close
                    </Button>
                </Modal.Body>
            </Modal>

            {/* Order Details Modal */}
            <Modal show={showOrderDetailsModal} onHide={() => setShowOrderDetailsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Order Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedOrder && (
                        <div>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Card className="h-100">
                                        <Card.Body>
                                            <Card.Title>Order Information</Card.Title>
                                            <p><strong>Order No:</strong> {selectedOrder.order_number}</p>
                                            <p><strong>Type:</strong> {getTypeLabel(selectedOrder.type)}</p>
                                            <p><strong>Status:</strong> {getStatusBadge(selectedOrder.status)}</p>
                                            <p><strong>Date:</strong> {new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className="h-100">
                                        <Card.Body>
                                            <Card.Title>Parties Involved</Card.Title>
                                            <p><strong>Customer:</strong> {selectedOrder.customer_name || 'N/A'}</p>
                                            <p><strong>Vendor:</strong> {selectedOrder.vendor_name || 'N/A'}</p>
                                            <p><strong>Driver:</strong> {selectedOrder.driver_name || 'Unassigned'}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Card className="h-100">
                                        <Card.Body>
                                            <Card.Title>Locations</Card.Title>
                                            <p><strong>Pickup:</strong> {selectedOrder.pickup_address}</p>
                                            <p><strong>Drop:</strong> {selectedOrder.drop_address}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className="h-100">
                                        <Card.Body>
                                            <Card.Title>Financial Details</Card.Title>
                                            <p><strong>Currency:</strong> {selectedOrder.currency || 'AED'}</p>
                                            <p><strong>Amount:</strong> {selectedOrder.total_amount || selectedOrder.return_value || '0.00'}</p>
                                            <p><strong>Remarks:</strong> {selectedOrder.remarks || 'None'}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            
                            <Card className="mb-3">
                                <Card.Title>Attachments</Card.Title>
                                <Card.Body>
                                    {selectedOrder.attachments &&
                                        (selectedOrder.attachments.pod_photos?.length ||
                                            selectedOrder.attachments.voice_notes?.length ||
                                            selectedOrder.attachments.order_docs?.length) ? (
                                        <Row>
                                            {selectedOrder.attachments.pod_photos?.map((file, idx) => (
                                                <Col md={4} key={`pod_${idx}`} className="mb-3">
                                                    <Card>
                                                        <Card.Body>
                                                            <Card.Title className="h6">POD Photo</Card.Title>
                                                            <p>{file.name || `File ${idx + 1}`}</p>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                onClick={() => handleOpenAttachment(file)}
                                                            >
                                                                <i className="fa fa-eye me-1"></i> View
                                                            </Button>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                            {selectedOrder.attachments.voice_notes?.map((file, idx) => (
                                                <Col md={4} key={`voice_${idx}`} className="mb-3">
                                                    <Card>
                                                        <Card.Body>
                                                            <Card.Title className="h6">Voice Note</Card.Title>
                                                            <p>{file.name || `File ${idx + 1}`}</p>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                onClick={() => handleOpenAttachment(file)}
                                                            >
                                                                <i className="fa fa-eye me-1"></i> View
                                                            </Button>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                            {selectedOrder.attachments.order_docs?.map((file, idx) => (
                                                <Col md={4} key={`doc_${idx}`} className="mb-3">
                                                    <Card>
                                                        <Card.Body>
                                                            <Card.Title className="h6">Document</Card.Title>
                                                            <p>{file.name || `File ${idx + 1}`}</p>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                onClick={() => handleOpenAttachment(file)}
                                                            >
                                                                <i className="fa fa-eye me-1"></i> View
                                                            </Button>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))}
                                        </Row>
                                    ) : (
                                        <p className="text-muted">No attachments available</p>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOrderDetailsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Attachment Modal */}
            <Modal show={showAttachmentModal} onHide={() => setShowAttachmentModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{currentAttachment?.name || currentAttachment?.type}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentAttachment?.type?.includes("image") ? (
                        <img src={currentAttachment?.url} alt={currentAttachment?.name} style={{ width: "100%" }} />
                    ) : (
                        <iframe
                            src={currentAttachment?.url}
                            title={currentAttachment?.name}
                            width="100%"
                            height="500px"
                        ></iframe>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAttachmentModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* OrderTracking Modal - Exactly like AdminDashboard */}
            <OrderTracking 
                show={showTrackModal} 
                handleClose={handleCloseTrackModal} 
                driverId={trackDriverId} 
                order={trackOrder}
            />
            
            <div 
  className="container justify-content-center align-items-center" 
  style={{ maxWidth: "40000px", background: "#f0f5ff", minHeight: "100vh", position: "relative", overflow: "hidden", padding: "20px 0" }} // adjust width as needed
>
                {/* Abstract Background Shapes */}
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: "none" }}>
                    {/* Large abstract shapes */}
                    <div style={{ position: "absolute", width: "300px", height: "300px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "-100px", left: "-50px", animation: "float 20s infinite ease-in-out" }}></div>
                    <div style={{ position: "absolute", width: "250px", height: "250px", borderRadius: "50%", background: "rgba(186, 230, 253, 0.5)", top: "20%", right: "-80px", animation: "float 20s infinite ease-in-out", animationDelay: "3s" }}></div>
                    <div style={{ position: "absolute", width: "200px", height: "200px", borderRadius: "50%", background: "rgba(224, 242, 254, 0.6)", bottom: "10%", left: "10%", animation: "float 20s infinite ease-in-out", animationDelay: "6s" }}></div>
                    <div style={{ position: "absolute", width: "180px", height: "180px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.3)", top: "50%", left: "50%", transform: "translate(-50%, -50%)", animation: "float 20s infinite ease-in-out", animationDelay: "9s" }}></div>
                    <div style={{ position: "absolute", width: "150px", height: "150px", borderRadius: "50%", background: "rgba(186, 230, 253, 0.4)", bottom: "-50px", right: "20%", animation: "float 20s infinite ease-in-out", animationDelay: "12s" }}></div>
                    <div style={{ position: "absolute", width: "120px", height: "120px", borderRadius: "50%", background: "rgba(224, 242, 254, 0.5)", top: "70%", right: "10%", animation: "float 20s infinite ease-in-out", animationDelay: "15s" }}></div>
                    
                    {/* Medium dots */}
                    <div style={{ position: "absolute", width: "80px", height: "80px", borderRadius: "50%", background: "rgba(30, 64, 175, 0.5)", top: "12%", left: "30%", animation: "floatMediumDot 18s infinite ease-in-out" }}></div>
                    <div style={{ position: "absolute", width: "70px", height: "70px", borderRadius: "50%", background: "rgba(37, 99, 235, 0.45)", top: "35%", left: "65%", animation: "floatMediumDot 18s infinite ease-in-out", animationDelay: "2s" }}></div>
                    <div style={{ position: "absolute", width: "85px", height: "85px", borderRadius: "50%", background: "rgba(59, 130, 246, 0.5)", top: "55%", left: "20%", animation: "floatMediumDot 18s infinite ease-in-out", animationDelay: "4s" }}></div>
                    <div style={{ position: "absolute", width: "75px", height: "75px", borderRadius: "50%", background: "rgba(96, 165, 250, 0.4)", top: "25%", left: "75%", animation: "floatMediumDot 18s infinite ease-in-out", animationDelay: "1s" }}></div>
                    <div style={{ position: "absolute", width: "90px", height: "90px", borderRadius: "50%", background: "rgba(30, 64, 175, 0.45)", top: "65%", left: "50%", animation: "floatMediumDot 18s infinite ease-in-out", animationDelay: "3s" }}></div>
                    <div style={{ position: "absolute", width: "65px", height: "65px", borderRadius: "50%", background: "rgba(37, 99, 235, 0.4)", top: "8%", left: "55%", animation: "floatMediumDot 18s infinite ease-in-out", animationDelay: "5s" }}></div>
                    <div style={{ position: "absolute", width: "80px", height: "80px", borderRadius: "50%", background: "rgba(59, 130, 246, 0.45)", top: "45%", left: "10%", animation: "floatMediumDot 18s infinite ease-in-out", animationDelay: "1.5s" }}></div>
                    <div style={{ position: "absolute", width: "70px", height: "70px", borderRadius: "50%", background: "rgba(96, 165, 250, 0.5)", top: "72%", left: "70%", animation: "floatMediumDot 18s infinite ease-in-out", animationDelay: "3.5s" }}></div>
                    
                    {/* Small dots */}
                    <div style={{ position: "absolute", width: "40px", height: "40px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "15%", left: "20%", animation: "floatDot 15s infinite ease-in-out" }}></div>
                    <div style={{ position: "absolute", width: "35px", height: "35px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "25%", left: "60%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "1s" }}></div>
                    <div style={{ position: "absolute", width: "30px", height: "30px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "35%", left: "15%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "2s" }}></div>
                    <div style={{ position: "absolute", width: "45px", height: "45px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "45%", left: "70%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "0.5s" }}></div>
                    <div style={{ position: "absolute", width: "35px", height: "35px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "55%", left: "30%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "1.5s" }}></div>
                    <div style={{ position: "absolute", width: "40px", height: "40px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "65%", left: "80%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "2.5s" }}></div>
                    <div style={{ position: "absolute", width: "30px", height: "30px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "20%", left: "45%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "3s" }}></div>
                    <div style={{ position: "absolute", width: "35px", height: "35px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "30%", left: "10%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "0.8s" }}></div>
                    <div style={{ position: "absolute", width: "40px", height: "40px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "40%", left: "55%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "1.8s" }}></div>
                    <div style={{ position: "absolute", width: "30px", height: "30px", borderRadius: "50%", background: "rgba(147, 197, 253, 0.4)", top: "50%", left: "25%", animation: "floatDot 15s infinite ease-in-out", animationDelay: "2.8s" }}></div>
                </div>
                <div className="row" style={{ position: "relative", zIndex: 1 }}>
                  <div 
  className="col-md-12"
  style={{ margin: "0px auto", maxWidth: "1400px" }} // adjust width as needed
>
    <div
  className="row mt-20"
  style={{
    minHeight: "300px", // increase the height as needed
    width: "100%",       // take full width of parent container
    display: "flex",
    flexWrap: "nowrap",  // prevent buttons from wrapping to next line
    alignItems: "flex-start",
    gap: "10px",         // optional: spacing between columns
    transition: "all 0.3s ease-in-out"
  }}
>

                        <div className="card shadow" style={{ background: "#ffffff" }}>
                            <div className="card-body">
                                <div className="container">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h2>Sparemate Auto Spare Parts Trading L.L.C</h2>
                                        <Button variant="outline-danger" onClick={handleLogout}>
                                            <FaSignOutAlt className="me-1" /> Logout
                                        </Button>
                                    </div>
                                    <div className="gap-3 mb-4" style={{ paddingTop: "20px" }}>
                                        <div className="row g-2 mt-20">
                                            <div className="col-lg col-md-4">
                                                <Button 
                                                    className={`for_btn ${activeForm === "formA" && activeView === 'form' ? "active" : ""}`} 
                                                    variant="primary" 
                                                    onClick={() => { 
                      setActiveView('form');
                      setActiveForm("formA"); 
                      setFormData(initialFormData); 
                      setFromCoords(null); 
                      setDropCoords(null); 
                      setToCoords(null);
                      setShowCustomerForm(false);
                      setShowVendorForm(false);
                      setShowOrderTrack(false);
                                                    }}
                                                >
                                                    <i className="fa fa-plus"></i> Purchase
                                                </Button>
                                            </div> 
                                            <div className="col-lg col-md-4">
                                                <Button 
                                                    className={`for_btn ${activeForm === "formB" && activeView === 'form' ? "active" : ""}`} 
                                                    variant="success" 
                                                    onClick={() => { 
                      setActiveView('form');
                      setActiveForm("formB"); 
                      setFormData(initialFormData); 
                      setFromCoords(null); 
                      setDropCoords(null); 
                      setToCoords(null);
                      setShowCustomerForm(false);
                      setShowVendorForm(false);
                      setShowOrderTrack(false);
                                                    }}
                                                >
                                                    <i className="fa fa-clock"></i> Sales Return
                                                </Button>
                                            </div> 
                                            <div className="col-lg col-md-4">
                                                <Button 
                                                    className={`for_btn ${activeForm === "formC" && activeView === 'form' ? "active" : ""}`} 
                                                    variant="warning" 
                                                    onClick={() => { 
                      setActiveView('form');
                      setActiveForm("formC"); 
                      setFormData(initialFormData); 
                      setFromCoords(null); 
                      setDropCoords(null); 
                      setToCoords(null);
                      setShowCustomerForm(false); 
                      setShowOrderTrack(false);
                      setShowVendorForm(false);
                                                    }}
                                                >
                                                    <i className="fa fa-check-circle"></i> Purchase Return
                                                </Button>
                                            </div> 
                                            <div className="col-lg col-md-4">
                                                <Button 
                                                    className={`for_btn ${activeView === 'history' ? "active" : ""}`} 
                                                    variant="info" 
                                                   onClick={() => {
                      setActiveView('history');
                      setShowCustomerForm(false);
                      setShowVendorForm(false);
                      setShowOrderTrack(false);
                    }}
                                                >
                                                    <i className="fa fa-history"></i> Sales History
                                                </Button>
                                            </div> 
                                            <div className="col-lg col-md-4">
    <Button
      className="for_btn"
      variant="success"
      onClick={() => {
        setShowOrderTrack(false);
                      setShowCustomerForm(true);
                      setShowVendorForm(false);
                      setActiveForm(null); // hide other forms
                      setActiveView(null); // hide other pages
      }}
    >
      <i className="fa fa-plus"></i> Add Customer
    </Button>
  </div> 
                                            <div className="col-lg col-md-4">
    <Button
      className="for_btn"
      variant="success"
      onClick={() => {
                      setShowVendorForm(true);
                      setShowCustomerForm(false);
                      setShowOrderTrack(false);
                      setActiveForm(null); // hide other forms
                      setActiveView(null); // hide other pages

      }}
    >
      <i className="fa fa-plus"></i> Add Vendor
    </Button>
  </div> 
                                            <div className="col-lg col-md-4">
    <Button
      className="for_btn"
      variant="success"
      onClick={() => {
                      setShowOrderTrack(true);
                      setShowVendorForm(false);
                      setShowCustomerForm(false);
                      setActiveForm(null); // hide other forms
                      setActiveView(null); // hide other pages

      }}
    > <i className="fa fa-map-marker-alt"></i> Order Tracking
  </Button>
</div>
                                        </div>
                                    </div>

{showCustomerForm && (
  <div className="mt-4">
    <h4 className="mb-3">Add Customer</h4>
   
      <AdminCustomers onClose={() => setShowCustomerForm(false)} />
   
  </div>
)}

{showVendorForm && (
  <div className="mt-4">
    <h4 className="mb-3">Add Vendor</h4>
   
      <AdminVendors onClose={() => setShowVendorForm(false)} />
 
  </div>
)}

{showOrderTrack && (
  <div className="mt-4">
    <h4 className="mb-3">Order Tracking</h4>
   
      <SalesFormOrderTrack onClose={() => setShowOrderTrack(false)} />
 
  </div>
)}
                                    </div>

                                    {activeView === 'history' ? (
                                        <SalesHistoryTable />
                                    ) : (
                                        <>
                                            {/* FORM A (Purchase) */}
                                            {activeForm === "formA" && (
                                                <div className="row">
                                                    <div className="container mt-4">
                                                        <Form onSubmit={handleSubmit}>
                                                            <Form.Group className="mb-3" controlId="po">
                                                                <Form.Label>PO</Form.Label>
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="Enter PO"
                                                                    name="po"
                                                                    value={formData.po}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="document">
                                                                <Form.Label>Attach Document</Form.Label>
                                                                <Form.Control
                                                                    type="file"
                                                                    name="document"
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="vendor">
                                                                <Form.Label>Vendor</Form.Label>
                                                                <AutocompleteDropdown
                                                                    options={vendors}
                                                                    value={formData.vendor}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        const selectedVendor = vendors.find(v => v.id == e.target.value);
                                                                        if (selectedVendor) {
                                                                            console.log("📍 Vendor selected:", selectedVendor);
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                fromLocation: selectedVendor.address || "",
                                                                                fromLatitude: selectedVendor.lat || "",
                                                                                fromLongitude: selectedVendor.lng || ""
                                                                            }));
                                                                            if (selectedVendor.lat && selectedVendor.lng) setFromCoords([selectedVendor.lat, selectedVendor.lng]);
                                                                        }
                                                                    }}
                                                                    placeholder="Search and select vendor..."
                                                                    name="vendor"
                                                                    displayField="name"
                                                                    valueField="id"
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="fromLocation">
                                                                <Form.Label>From Location</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={2}
                                                                    placeholder="Enter from location"
                                                                    name="fromLocation"
                                                                    value={formData.fromLocation}
                                                                    onChange={handleChange}
                                                                />
                                                                <Row className="mt-2">
                                                                   {/* <Col md={6}>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Latitude"
                                                                            name="fromLatitude"
                                                                            value={formData.fromLatitude}
                                                                            onChange={handleChange}
                                                                            step="any"
                                                                        />
                                                                    </Col>*/}
                                                                   {/*  <Col md={6}>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Longitude"
                                                                            name="fromLongitude"
                                                                            value={formData.fromLongitude}
                                                                            onChange={handleChange}
                                                                            step="any"
                                                                        />
                                                                    </Col>*/}
                                                                </Row>
                                                               {/* <Button
                                                                    variant="primary"
                                                                    className="mt-2 for_mapbtn"
                                                                    onClick={() => setShowFromMap(true)}
                                                                >
                                                                    Search on Map
                                                                </Button>*/}
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="toLocation">
                                                                <Form.Label>To Location (Showroom)</Form.Label>
                                                                <AutocompleteDropdown
                                                                    options={showroomses}
                                                                    value={formData.toLocation || ""}
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        const selectedShowroom = showroomses.find(s => s.id == e.target.value);
                                                                        if (selectedShowroom) {
                                                                            console.log("🏢 Showroom selected:", selectedShowroom);
                                                                            setFormData(prev => ({
                                                                                ...prev,
                                                                                dropLocationDetails: selectedShowroom.address || "",
                                                                                dropLatitude: selectedShowroom.lat || "",
                                                                                dropLongitude: selectedShowroom.lng || ""
                                                                            }));
                                                                            if (selectedShowroom.lat && selectedShowroom.lng) setDropCoords([selectedShowroom.lat, selectedShowroom.lng]);
                                                                        }
                                                                    }}
                                                                    placeholder="Search and select showroom..."
                                                                    name="toLocation"
                                                                    displayField="name"
                                                                    valueField="id"
                                                                />
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="dropLocation">
                                                                <Form.Label>Drop Location</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={2}
                                                                    placeholder="Enter drop location details"
                                                                    name="dropLocationDetails"
                                                                    value={formData.dropLocationDetails || ""}
                                                                    onChange={handleChange}
                                                                    className="mt-2"
                                                                />
                                                                <Row className="mt-2">
                                                                    {/*<Col md={6}>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Latitude"
                                                                            name="dropLatitude"
                                                                            value={formData.dropLatitude}
                                                                            onChange={handleChange}
                                                                            step="any"
                                                                        />
                                                                    </Col>*/}
                                                                     {/*<Col md={6}>
                                                                        <Form.Control
                                                                            type="number"
                                                                            placeholder="Longitude"
                                                                            name="dropLongitude"
                                                                            value={formData.dropLongitude}
                                                                            onChange={handleChange}
                                                                            step="any"
                                                                        />
                                                                    </Col>*/}
                                                                </Row>
                                                               {/*  <Button
                                                                    variant="primary"
                                                                    className="mt-2 for_mapbtn"
                                                                    onClick={() => setShowDropMap(true)}
                                                                >
                                                                    Search on Map
                                                                </Button>*/}
                                                            </Form.Group>

                                                            <Form.Group className="mb-3" controlId="remarks">
                                                                <Form.Label>Remarks</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={2}
                                                                    placeholder="Enter remarks"
                                                                    name="remarks"
                                                                    value={formData.remarks}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Group>

                                                            <Button
                                                                type="submit"
                                                                style={{ width: "100%" }}
                                                                className="for_mapbtn"
                                                                variant="success"
                                                            >
                                                                Submit
                                                            </Button>
                                                        </Form>

                                                        <MapPopup
                                                            show={showFromMap}
                                                            handleClose={() => setShowFromMap(false)}
                                                            title="Select From Location"
                                                            selectedLocation={fromCoords}
                                                            setSelectedLocation={setFromCoords}
                                                            setAddress={handleFromMapSelect}
                                                        />
                                                        <MapPopup
                                                            show={showDropMap}
                                                            handleClose={() => setShowDropMap(false)}
                                                            title="Select Drop Location"
                                                            selectedLocation={dropCoords}
                                                            setSelectedLocation={setDropCoords}
                                                            setAddress={handleDropMapSelect}
                                                        />
                                                    </div>
                                                </div>
                                            )}


                                            {/* FORM B (Sales Return) */}
                                            {activeForm === "formB" && (
                                                <div className="container mt-4">
                                                    <Form onSubmit={handleSubmit}>
                                                        {/* Customer Selection */}
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Customer</Form.Label>
                                                            <AutocompleteDropdown
                                                                options={customers}
                                                                value={formData.customer || ""}
                                                                onChange={(e) => {
                                                                    const customerId = e.target.value ? parseInt(e.target.value, 10) : "";
                                                                    handleChange({ target: { name: "customer", value: customerId } });
                                                                }}
                                                                placeholder="Search and select customer..."
                                                                name="customer"
                                                                displayField="name"
                                                                valueField="id"
                                                            />
                                                        </Form.Group>

                                                        {/* PO */}
                                                        <Form.Group>
                                                            <Form.Label>Order No.</Form.Label>
                                                            <Form.Select
                                                                value={selectedOrderId}
                                                                onChange={(e) => {
                                                                    const orderId = e.target.value ? parseInt(e.target.value, 10) : "";
                                                                    setSelectedOrderId(orderId);
                                                                }}
                                                            >
                                                                <option value="">-- Select Order --</option>
                                                                {customerOrders.map(order => (
                                                                    <option key={order.id} value={order.id}>
                                                                        {order.po_number || `Order #${order.id}`}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>


                                                        {/* Sales Return (Order Number) */}
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Sales Return (Order Number)</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                name="salesReturn"
                                                                value={formData.salesReturn}
                                                                onChange={handleChange}
                                                                placeholder="Enter Order Number"
                                                            />

                                                        </Form.Group>


                                                        {/* From Location */}
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>From Location</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                placeholder="Pickup address"
                                                                name="fromLocation"
                                                                value={formData.fromLocation}
                                                                onChange={handleChange}
                                                            />
                                                            <Row className="mt-2">
                                                                {/* <Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Latitude"
                                                                        name="fromLatitude"
                                                                        value={formData.fromLatitude}
                                                                        onChange={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                               {/* <Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Longitude"
                                                                        name="fromLongitude"
                                                                        value={formData.fromLongitude}
                                                                        onChange={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                            </Row>
                                                            {/*<Button
                                                                variant="primary"
                                                                className="mt-2 for_mapbtn"
                                                                onClick={() => setShowFromMap(true)}
                                                            >
                                                                Search on Map
                                                            </Button>*/}
                                                        </Form.Group>

                                                        {/* To Location (Showroom) */}
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>To Location (Showroom)</Form.Label>
                                                            <AutocompleteDropdown
                                                                options={showroomses}
                                                                value={formData.toLocation || ""}
                                                                onChange={(e) => {
                                                                    handleChange(e);
                                                                    const selectedShowroom = showroomses.find(s => s.id == e.target.value);
                                                                    if (selectedShowroom) {
                                                                        console.log("🏢 Showroom selected:", selectedShowroom);
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            dropLocationDetails: selectedShowroom.address || "",
                                                                            dropLatitude: selectedShowroom.lat || "",
                                                                            dropLongitude: selectedShowroom.lng || ""
                                                                        }));
                                                                        if (selectedShowroom.lat && selectedShowroom.lng) {
                                                                            setDropCoords([selectedShowroom.lat, selectedShowroom.lng]);
                                                                        }
                                                                    }
                                                                }}
                                                                placeholder="Search and select showroom..."
                                                                name="toLocation"
                                                                displayField="name"
                                                                valueField="id"
                                                            />
                                                            
                                                        </Form.Group>
                                                        <Form.Group className="mb-3" controlId="dropLocation">
                                                            <Form.Label>Drop Location</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                placeholder="Enter drop location details"
                                                                name="dropLocationDetails"
                                                                value={formData.dropLocationDetails || ""}
                                                                onChange={handleChange}
                                                                className="mt-2"
                                                            />
                                                            <Row className="mt-2">
                                                                {/*<Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Latitude"
                                                                        name="dropLatitude"
                                                                        value={formData.dropLatitude}
                                                                        onChange={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                               {/* <Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Longitude"
                                                                        name="dropLongitude"
                                                                        value={formData.dropLongitude}
                                                                        onChange={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                            </Row>
                                                            {/*<Button
                                                                variant="primary"
                                                                className="mt-2 for_mapbtn"
                                                                onClick={() => setShowDropMap(true)}
                                                            >
                                                                Search on Map
                                                            </Button>*/}
                                                        </Form.Group>

                                                        {/* Return Value */}
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Return Value</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                placeholder="Enter return value"
                                                                name="returnValue"
                                                                value={formData.returnValue}
                                                                onChange={handleChange}
                                                            />
                                                        </Form.Group>

                                                        {/* Remarks */}
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Remarks</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                placeholder="Enter remarks"
                                                                name="remarks"
                                                                value={formData.remarks}
                                                                onChange={handleChange}
                                                            />
                                                        </Form.Group>

                                                        <Button
                                                            type="submit"
                                                            style={{ width: "100%" }}
                                                            className="for_mapbtn"
                                                            variant="success"
                                                        >
                                                            Submit
                                                        </Button>
                                                    </Form>

                                                    {/* Map Popups */}
                                                    <MapPopup
                                                        show={showFromMap}
                                                        handleClose={() => setShowFromMap(false)}
                                                        title="Select From Location"
                                                        selectedLocation={fromCoords}
                                                        setSelectedLocation={setFromCoords}
                                                        setAddress={handleFromMapSelect}
                                                    />
                                                    <MapPopup
                                                        show={showDropMap}
                                                        handleClose={() => setShowDropMap(false)}
                                                        title="Select Drop Location"
                                                        selectedLocation={dropCoords}
                                                        setSelectedLocation={setDropCoords}
                                                        setAddress={handleDropMapSelect}
                                                    />
                                                </div>
                                            )}


                                            {/* FORM C (Purchase Return) */}
                                            {activeForm === "formC" && (
                                                <div className="container mt-4">
                                                    <Form onSubmit={handleSubmit}>
                                                        <Form.Group className="mb-3" controlId="vendor">
                                                            <Form.Label>Vendor</Form.Label>
                                                            <AutocompleteDropdown
                                                                options={vendors}
                                                                value={formData.vendor}
                                                                onChange={handleChange}
                                                                placeholder="Search and select vendor..."
                                                                name="vendor"
                                                                displayField="name"
                                                                valueField="id"
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>PO #</Form.Label>
                                                            <AutocompleteDropdown
                                                                options={poNumbers.filter(po => po.vendor_id === formData.vendor)}
                                                                value={formData.poId || ""}
                                                                onChange={handlePurchasePoSelect}
                                                                placeholder="Search and select PO..."
                                                                name="poId"
                                                                displayField="po_number"
                                                                valueField="id"
                                                                required
                                                            />
                                                        </Form.Group>


                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Invoice Number</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Enter Invoice Number"
                                                                name="invoiceNumber"
                                                                value={formData.invoiceNumber || ""}
                                                                onChange={handleChange}
                                                                required
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Return Value </Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                name="returnValue"
                                                                value={formData.returnValue || ""}
                                                                onChange={handleChange}
                                                                required
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Attach Document</Form.Label>
                                                            <Form.Control
                                                                type="file"
                                                                name="document"
                                                                onChange={handleChange}
                                                            />
                                                        </Form.Group>
                                                        {/* From Location (Showroom) */}
                                                        <Form.Group className="mb-3" controlId="fromLocations">
                                                            <Form.Label>From Location (Showroom)</Form.Label>
                                                            <AutocompleteDropdown
                                                                options={showroomses}
                                                                value={formData.fromLocations || ""}
                                                                onChange={handleChange}
                                                                placeholder="Search and select showroom..."
                                                                name="fromLocations"
                                                                displayField="name"
                                                                valueField="id"
                                                            />
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>From Location </Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                name="fromLocation"
                                                                value={formData.fromLocation}
                                                                onChange={handleChange}
                                                                required
                                                            />
                                                            <Row className="mt-2">
                                                               {/* <Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Latitude"
                                                                        name="fromLatitude"
                                                                        value={formData.fromLatitude}
                                                                        onChange={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                               {/* <Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Longitude"
                                                                        name="fromLongitude"
                                                                        value={formData.fromLongitude}
                                                                        onChange={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                            </Row>
                                                            {/*<Button
                                                                className="mt-2 for_mapbtn"
                                                                variant="primary"
                                                                onClick={() => setShowFromMap(true)}
                                                            >
                                                                Search on Map
                                                            </Button>*/}
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>To Location</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                name="toLocation"
                                                                value={formData.toLocation || ""}
                                                                onChange={handleChange}
                                                            />
                                                            <Row className="mt-2">
                                                               {/*  <Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Latitude"
                                                                        name="toLatitude"
                                                                        value={formData.toLatitude}
                                                                        onChange={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                               {/*  <Col md={6}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Longitude"
                                                                        name="toLongitude"
                                                                        value={formData.toLongitude}
                                                                        on
                                                                        Change={handleChange}
                                                                        step="any"
                                                                    />
                                                                </Col>*/}
                                                            </Row>
                                                           {/*  <Button
                                                                className="mt-2 for_mapbtn"
                                                                variant="primary"
                                                                onClick={() => setShowToMap(true)}
                                                            >
                                                                Search on Map
                                                            </Button>*/}
                                                        </Form.Group>

                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Remarks</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                name="remarks"
                                                                value={formData.remarks}
                                                                onChange={handleChange}
                                                            />
                                                        </Form.Group>

                                                        <Button
                                                            type="submit"
                                                            style={{ width: "100%" }}
                                                            className="for_mapbtn"
                                                            variant="success"
                                                        >
                                                            Submit
                                                        </Button>
                                                    </Form>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <MapPopup
                show={showFromMap}
                handleClose={() => setShowFromMap(false)}
                title="Select From Location"
                selectedLocation={fromCoords}
                setSelectedLocation={setFromCoords}
                setAddress={handleFromMapSelect}
            />
            <MapPopup
                show={showDropMap}
                handleClose={() => setShowDropMap(false)}
                title="Select Drop Location"
                selectedLocation={dropCoords}
                setSelectedLocation={setDropCoords}
                setAddress={handleDropMapSelect}
            />
            <MapPopup
                show={showToMap}
                handleClose={() => setShowToMap(false)}
                title="Select To Location"
                selectedLocation={toCoords}
                setSelectedLocation={setToCoords}
                setAddress={handleToMapSelect}
            />
        </div>
    );
};

export default SalesForm;
export { PublicTrackingPage };